/** Document-level Graph AI controls and related documents. */

import { useNavigate } from 'react-router-dom'
import { Network, Play, RefreshCw } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  useDocumentGraphExtraction,
  useRunDocumentGraphExtraction,
} from '../../hooks/use-graph-extraction'

interface Props {
  documentId: string
  isDark: boolean
}

export function DocumentKnowledgeExtractionPanel({ documentId, isDark }: Props) {
  const navigate = useNavigate()
  const { data, isLoading } = useDocumentGraphExtraction(documentId)
  const runExtraction = useRunDocumentGraphExtraction(documentId)
  const latest = data?.latestJob

  const mutedCls = cn('text-[11px]', isDark ? 'text-neutral-500' : 'text-neutral-400')
  const itemCls = cn('rounded px-2 py-1 text-left text-xs', isDark ? 'hover:bg-neutral-800' : 'hover:bg-neutral-100')

  return (
    <div className={cn('space-y-2 border-t pt-3', isDark ? 'border-neutral-800' : 'border-neutral-200')}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Network className="h-3.5 w-3.5 text-neutral-500" />
          <h3 className={cn('text-xs font-semibold uppercase tracking-wider', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
            Knowledge Graph
          </h3>
        </div>
        <button
          type="button"
          onClick={() => runExtraction.mutate()}
          disabled={runExtraction.isPending}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[11px] font-medium disabled:opacity-50',
            isDark ? 'bg-neutral-800 text-neutral-200 hover:bg-neutral-700' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
          )}
          title="Run graph extraction"
        >
          {runExtraction.isPending ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          Extract
        </button>
      </div>

      <div className={mutedCls}>
        {isLoading
          ? 'Loading graph status...'
          : latest
            ? `${latest.status} · ${latest.trigger} · ${latest.entityCount} entities · ${latest.docRelationshipCount} docs`
            : 'No extraction history yet'}
      </div>

      {(data?.relatedDocs ?? []).length > 0 && (
        <div className="space-y-1">
          <div className={mutedCls}>Related docs</div>
          {data!.relatedDocs.slice(0, 6).map((doc) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => navigate(`/doc/${doc.id}`)}
              className={cn(itemCls, 'flex w-full items-center justify-between gap-2')}
            >
              <span className={cn('truncate', isDark ? 'text-neutral-300' : 'text-neutral-700')}>{doc.title}</span>
              <span className={cn('shrink-0 text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>{Math.round(doc.score * 100)}%</span>
            </button>
          ))}
        </div>
      )}

      {(data?.entities ?? []).length > 0 && (
        <div className="space-y-1">
          <div className={mutedCls}>Entities</div>
          <div className="flex flex-wrap gap-1">
            {data!.entities.slice(0, 10).map((entity) => (
              <span
                key={entity.id}
                className={cn(
                  'max-w-full truncate rounded px-1.5 py-0.5 text-[10px]',
                  isDark ? 'bg-neutral-800 text-neutral-300' : 'bg-neutral-100 text-neutral-600',
                )}
                title={`${entity.name} · ${entity.type}`}
              >
                {entity.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
